import React, { Suspense, startTransition } from 'react';
import { NavLink, HashRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Authenticator, useAuthenticator, TextField, Button } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaHome, FaMap, FaUser, FaCheckCircle, FaBullhorn, FaDownload, FaPlus, FaUpload, FaImage, FaHandshake, FaBars, FaTimes } from 'react-icons/fa';
import { Advertising, EditAdvertising } from './components/Advertising';
import { UI_CONFIG } from './config';
import ErrorBoundary from './components/ErrorBoundary';
import VersionFooter from './components/VersionFooter';
import useAuthStore from './stores/useAuthStore';
import {
  signInWithEmailOTP,
  signInWithUserPassword,
  confirmSignInWithOTP,
  clearAuthData,
} from './services/authService';
import { setAuthRefreshFailureHandler } from './services/authSession';
import { getCognitoAuthMode } from './config/cognitoAuth';
import { getSavedEmail, getSavedUsername } from './utils/emailStorage';
import { usePermissions } from './hooks/usePermissions';
import lazyWithRetry from './utils/lazyWithRetry';
import './App.css';

const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const PublicDashboard = lazyWithRetry(() => import('./components/PublicDashboard'));
const InstallsDashboard = lazyWithRetry(() => import('./components/InstallsDashboard'));
const SponsorsPage = lazyWithRetry(() => import('./components/SponsorsPage'));
const Map = lazyWithRetry(() => import('./components/Map'));
const Playgrounds = lazyWithRetry(() => import('./components/Playgrounds'));
const EditApprovals = lazyWithRetry(() => import('./components/EditApprovals'));
const NewItemApprovals = lazyWithRetry(() => import('./components/NewItemApprovals'));
const ImageReviews = lazyWithRetry(() => import('./components/ImageReviews'));
const AddUpdateItems = lazyWithRetry(() => import('./components/AddUpdateItems'));
const EditItem = lazyWithRetry(() => import('./components/EditItem'));
const ItemHistory = lazyWithRetry(() => import('./components/ItemHistory'));
const DuplicateComparison = lazyWithRetry(() => import('./components/DuplicateComparison'));
const ImageUpload = lazyWithRetry(() => import('./components/ImageUpload'));
const ImageMaker = lazyWithRetry(() => import('./components/ImageMaker'));

function HashRouteNormalizer() {
  React.useEffect(() => {
    const normalize = () => {
      const pathname = window.location.pathname;
      const fragment = window.location.hash.substring(1); // may be '', 'id=...', or '/playgrounds?...#id=...'
      const fragmentPath = fragment.split('?')[0].split('#')[0];

      // Only normalize for the /playgrounds page when using HashRouter
      if (pathname === '/playgrounds') {
        const searchParams = new URLSearchParams(window.location.search);
        const q = searchParams.get('q');

        // If the SPA hash already points at another screen (e.g. NavLink to /edit-approvals),
        // do not force the hash back to /playgrounds — that was stealing navigation from the menu.
        // Canonicalize the URL by moving routing into the hash only: /#/edit-approvals?...
        if (fragment && fragmentPath && !fragmentPath.startsWith('/playgrounds')) {
          window.history.replaceState(null, '', `/${window.location.hash}`);
          return;
        }

        // Try to read id from the trailing hash section if present
        let id = '';
        if (fragment) {
          const parts = fragment.split('#');
          const last = parts[parts.length - 1];
          const lastParams = new URLSearchParams(last);
          id = lastParams.get('id') || '';
        }

        // If the hash is missing or not pointing to /playgrounds, rewrite it to the canonical form
        if (!fragment || !fragmentPath.startsWith('/playgrounds')) {
          const nextQuery = new URLSearchParams();
          if (q) nextQuery.set('q', q);
          let newHash = '/playgrounds';
          const queryString = nextQuery.toString();
          if (queryString) newHash += `?${queryString}`;
          if (id) newHash += `#id=${encodeURIComponent(id)}`;
          if (window.location.hash.substring(1) !== newHash) {
            window.location.hash = newHash;
          }
        }
      }
    };

    // Normalize on load
    normalize();
    // Normalize on hash changes (e.g., pasted fragments)
    window.addEventListener('hashchange', normalize, false);
    return () => window.removeEventListener('hashchange', normalize, false);
  }, []);

  return null;
}

/** `#id=<itemId>` (legacy bookmarks) → `#/edit-item/<itemId>` */
function LegacyHashItemIdRedirect() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const run = () => {
      const raw = window.location.hash.substring(1);
      if (!raw || raw.startsWith('/')) return;
      const legacyId = new URLSearchParams(raw).get('id');
      if (!legacyId) return;
      navigate(`/edit-item/${encodeURIComponent(legacyId)}`, { replace: true });
    };
    run();
    window.addEventListener('hashchange', run);
    return () => window.removeEventListener('hashchange', run);
  }, [navigate]);

  return null;
}

// Custom SignIn component that only shows email field (no password) for Email OTP
const CustomSignInFormFields = () => {
  const { fields, handleSubmit, validationErrors } = useAuthenticator((context) => [
    context.fields,
    context.handleSubmit,
    context.validationErrors,
  ]);
  const savedEmail = getSavedEmail();

  // Pre-fill email if available
  React.useEffect(() => {
    if (savedEmail && fields.username && !fields.username.value) {
      fields.username.setValue(savedEmail);
    }
  }, [savedEmail, fields]);

  return (
    <>
      {/* Only render email field - no password field */}
      <TextField
        name="username"
        label="Email"
        placeholder="Enter your email"
        type="email"
        value={fields.username?.value || savedEmail || ''}
        onChange={(e) => {
          fields.username?.setValue(e.target.value);
        }}
        hasError={!!validationErrors.username}
        errorMessage={validationErrors.username}
        required
        autoComplete="email"
      />
    </>
  );
};

App.propTypes = {
  signOut: PropTypes.func,
  user: PropTypes.object // Assuming 'user' is an object, adjust as necessary
};

// Custom NavLink wrapper that uses startTransition for lazy-loaded routes
const TransitionNavLink = ({ to, className, title, children, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  const handleClick = (e) => {
    e.preventDefault();
    onClick?.();
    startTransition(() => {
      navigate(to);
    });
  };
  
  const linkClassName = isActive ? `${className} active` : className;
  
  return (
    <a href={`#${to}`} className={linkClassName} title={title} onClick={handleClick}>
      {children}
    </a>
  );
};

const AuthenticatedApp = ({ signOut, user }) => {
  const setUser = useAuthStore(state => state.setUser);
  const authStoreSignOut = useAuthStore(state => state.signOut);
  const groups = useAuthStore(state => state.groups);
  const { isAdmin, isManager } = usePermissions();
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = React.useState(false);
  const closeNav = () => setIsNavOpen(false);

  // Close the mobile nav drawer whenever the route changes
  React.useEffect(() => {
    closeNav();
  }, [location.pathname]);

  // Sync user from Amplify to Zustand store
  React.useEffect(() => {
    if (user) {
      console.log('🔥 App: Setting user in auth store:', user);
      setUser(user);
    }
  }, [user, setUser]);

  React.useEffect(() => {
    setAuthRefreshFailureHandler(async () => {
      authStoreSignOut();
      clearAuthData();
      await signOut();
    });
    return () => setAuthRefreshFailureHandler(null);
  }, [signOut, authStoreSignOut]);

  // Enhanced sign out that clears saved email
  const handleSignOut = async () => {
    clearAuthData();
    await signOut();
  };

  return (
    <ErrorBoundary>
            <div>
              <nav className="top-nav">
                <button
                  type="button"
                  className="nav-toggle"
                  aria-label={isNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
                  aria-expanded={isNavOpen}
                  onClick={() => setIsNavOpen((open) => !open)}
                >
                  {isNavOpen ? <FaTimes className="nav-icon" /> : <FaBars className="nav-icon" />}
                </button>
                <div className={`nav-links${isNavOpen ? ' open' : ''}`}>
                  <NavLink to="/" className="nav-item" title="Home" onClick={closeNav}>
                    <FaHome className="nav-icon" />
                    <span className="nav-text">Home</span>
                  </NavLink>
                  <NavLink to="/add-update-items" className="nav-item" title="Add/Update Items" onClick={closeNav}>
                    <FaPlus className="nav-icon" />
                    <span className="nav-text">Add/Update</span>
                  </NavLink>
                  <NavLink to="/upload-image" className="nav-item" title="Upload Image" onClick={closeNav}>
                    <FaUpload className="nav-icon" />
                    <span className="nav-text">Upload Image</span>
                  </NavLink>
                  <NavLink to="/map" className="nav-item" title="Map" onClick={closeNav}>
                    <FaMap className="nav-icon" />
                    <span className="nav-text">Map</span>
                  </NavLink>
                  <NavLink to="/imagemaker" className="nav-item" title="Image maker" onClick={closeNav}>
                    <FaImage className="nav-icon" />
                    <span className="nav-text">Image maker</span>
                  </NavLink>
                  <NavLink to="/advertising" className="nav-item" title="Advertising" onClick={closeNav}>
                    <FaBullhorn className="nav-icon" />
                    <span className="nav-text">Advertising</span>
                  </NavLink>
                  <NavLink to="/edit-approvals" className="nav-item" title="Approvals" onClick={closeNav}>
                    <FaCheckCircle className="nav-icon" />
                    <span className="nav-text">Approvals</span>
                  </NavLink>
                  <TransitionNavLink to="/installs" className="nav-item" title="Installs Dashboard" onClick={closeNav}>
                    <FaDownload className="nav-icon" />
                    <span className="nav-text">Installs</span>
                  </TransitionNavLink>
                  <TransitionNavLink to="/sponsors" className="nav-item" title="Supporters and partners" onClick={closeNav}>
                    <FaHandshake className="nav-icon" />
                    <span className="nav-text">Supporters</span>
                  </TransitionNavLink>
                </div>
                <div className="user-menu">
                  <button className="user-menu-button">
                    <span className="nav-text">
                      {user.username}
                      {groups.length > 0 && (
                        <span style={{ fontSize: '0.8em', marginLeft: '8px', opacity: 0.7 }}>
                          ({groups.join(', ')})
                        </span>
                      )}
                    </span>
                    <FaUser className="nav-icon" />
                  </button>
                  <div className="user-menu-dropdown">
                    {groups.length > 0 && (
                      <div style={{ padding: '8px 16px', borderBottom: '1px solid #ccc', fontSize: '0.9em', color: '#666' }}>
                        Groups: {groups.join(', ')}
                      </div>
                    )}
                    <button onClick={handleSignOut}>Sign Out</button>
                  </div>
                </div>
              </nav>
              {isNavOpen && <div className="nav-overlay" onClick={closeNav} />}
              <div className='mainContent'>
                <ErrorBoundary>
                  <Suspense fallback={<div className='mainContent'>Loading...</div>}>
                    <Routes>
                      <Route path="/" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                      <Route path="/map" element={<ErrorBoundary><Map /></ErrorBoundary>} />
                      <Route path="/imagemaker" element={<ErrorBoundary><ImageMaker /></ErrorBoundary>} />
                      <Route path="/playgrounds" element={<ErrorBoundary><Playgrounds/></ErrorBoundary>} />
                      <Route path="/edit-approvals" element={<ErrorBoundary><EditApprovals/></ErrorBoundary>} />
                      <Route path="/new-item-approvals" element={<ErrorBoundary><NewItemApprovals/></ErrorBoundary>} />
                      <Route path="/image-reviews" element={<ErrorBoundary><ImageReviews/></ErrorBoundary>} />
                      <Route path="/advertising" element={<ErrorBoundary><Advertising /></ErrorBoundary>} />
                      <Route path="/advertising/edit/:id" element={<ErrorBoundary><EditAdvertising /></ErrorBoundary>} />
                      <Route path="/installs" element={<ErrorBoundary><InstallsDashboard /></ErrorBoundary>} />
                      <Route path="/add-update-items" element={<ErrorBoundary><AddUpdateItems /></ErrorBoundary>} />
                      <Route path="/edit-item/*" element={<ErrorBoundary><EditItem /></ErrorBoundary>} />
                      <Route path="/item-history" element={<ErrorBoundary><ItemHistory /></ErrorBoundary>} />
                      <Route path="/duplicate-comparison" element={<ErrorBoundary><DuplicateComparison /></ErrorBoundary>} />
                      <Route path="/upload-image" element={<ErrorBoundary><ImageUpload /></ErrorBoundary>} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
                <ToastContainer {...UI_CONFIG.notifications} hideProgressBar={true}/>
              </div>
            </div>
    </ErrorBoundary>
  );
};

const hideAuthenticatorPasswordFieldCss = `
        [data-amplify-authenticator] input[type="password"],
        [data-amplify-authenticator] input[name="password"],
        [data-amplify-authenticator] label[for*="password" i],
        [data-amplify-authenticator] [data-amplify-password-field],
        [data-amplify-authenticator] .amplify-field-group:has(input[type="password"]),
        [data-amplify-authenticator] label:has(+ input[type="password"]),
        [data-amplify-authenticator] .amplify-field-group[data-amplify-password-field],
        [data-amplify-signin] input[type="password"],
        [data-amplify-signin] input[name="password"],
        [data-amplify-signin] label[for*="password" i],
        [data-amplify-signin] [data-amplify-password-field] {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          opacity: 0 !important;
          position: absolute !important;
          width: 0 !important;
          overflow: hidden !important;
        }
      `;

function CognitoSignInGate() {
  const authMode = getCognitoAuthMode();
  // Default to password (username/password) login; fall back to email only if env forces it
  const defaultMethod = authMode === 'email' ? 'email' : 'password';
  const [signInMethod, setSignInMethod] = React.useState(defaultMethod);
  const passwordUi = authMode === 'password' || (authMode === 'both' && signInMethod === 'password');

  const pickerButtonStyle = (active) => ({
    padding: '0.4rem 0.85rem',
    fontSize: '0.9rem',
    cursor: 'pointer',
    borderRadius: 6,
    border: active ? '2px solid var(--color-primary, #2563eb)' : '1px solid var(--color-border, #ccc)',
    background: active ? 'var(--color-surface-elevated, #f4f4f5)' : 'transparent',
    fontWeight: active ? 600 : 400,
  });

  return (
    <>
      {!passwordUi && <style>{hideAuthenticatorPasswordFieldCss}</style>}
      {authMode === 'both' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
            flexWrap: 'wrap',
          }}
          role="group"
          aria-label="Sign-in method"
        >
          <button
            type="button"
            style={pickerButtonStyle(signInMethod === 'password')}
            aria-pressed={signInMethod === 'password'}
            onClick={() => setSignInMethod('password')}
          >
            Username and password
          </button>
          <button
            type="button"
            style={pickerButtonStyle(signInMethod === 'email')}
            aria-pressed={signInMethod === 'email'}
            onClick={() => setSignInMethod('email')}
          >
            Email code
          </button>
        </div>
      )}
      <Authenticator
        key={passwordUi ? 'password' : 'email'}
        hideSignUp
        loginMechanisms={passwordUi ? ['username'] : ['email']}
        formFields={{
          signIn: {
            username: passwordUi
              ? {
                  placeholder: 'Username',
                  label: 'Username',
                  type: 'text',
                  defaultValue: getSavedUsername() || '',
                }
              : {
                  placeholder: 'Enter your email',
                  label: 'Email',
                  type: 'email',
                  defaultValue: getSavedEmail() || '',
                },
            password: {
              isRequired: passwordUi,
            },
          },
        }}
        {...(!passwordUi
          ? {
              components: {
                SignIn: {
                  FormFields: CustomSignInFormFields,
                },
              },
            }
          : {})}
        services={{
          async handleSignIn(input) {
            const { username, password } = input;
            if (passwordUi) {
              return signInWithUserPassword(username, password);
            }
            return signInWithEmailOTP(username);
          },
          async handleConfirmSignIn(input) {
            const { challengeResponse } = input;

            const result = await confirmSignInWithOTP(challengeResponse);

            return result;
          },
          cognitoUserPoolConfig: {
            deviceConfig: {
              deviceOnlyRememberedOnUserPrompt: true,
              deviceRemembered: true,
            },
          },
        }}
      >
        {({ signOut, user }) => <AuthenticatedApp signOut={signOut} user={user} />}
      </Authenticator>
    </>
  );
}

export function App() {
  return (
    <>
      <Router>
        <HashRouteNormalizer />
        <LegacyHashItemIdRedirect />
        <Routes>
          <Route path="/play" element={<Suspense fallback={<div>Loading...</div>}><ErrorBoundary><PublicDashboard /></ErrorBoundary></Suspense>} />
          <Route path="/installs" element={<Suspense fallback={<div>Loading...</div>}><ErrorBoundary><InstallsDashboard /></ErrorBoundary></Suspense>} />
          <Route path="/sponsors" element={<Suspense fallback={<div>Loading...</div>}><ErrorBoundary><SponsorsPage /></ErrorBoundary></Suspense>} />
          <Route path="*" element={<CognitoSignInGate />} />
        </Routes>
      </Router>
      <VersionFooter />
    </>
  );
}

function NotFoundPage() {
  return (
    <div>
      <h1>404 - Not Found</h1>
      <p>Sorry, the page you are looking for does not exist.</p>
    </div>
  );
}

export default App;
